import { LinkProps } from 'react-router-dom';

import { Box } from 'components/Box';
import { AppleButton, FacebookButton, GoogleButton } from 'components/Button';
import { Divider } from 'components/Divider';
import {
  Form,
  FormSubmitFunction,
  FormUsageContext,
  GeneralError,
  InputField,
  SubmitButton,
} from 'components/Form';
import { InfoBox } from 'components/InfoBox';
import { BodySmall } from 'components/Text/BodySmall';
import { deleteCookie } from 'utils/cookies';

import { LoginFormForgotPasswordLink } from './LoginFormForgotPasswordLink';
import { LoginFormSignupLink } from './LoginFormSignupLink';

type Props = {
  analyticsTitle: string;
  usageContext: FormUsageContext;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialValues: Record<string, any>;
  thirdPartyLoginError: string | null | undefined;
  showSignupModal: (email: string) => void;
  getSignupUrl: (email: string) => LinkProps['to'];
  signUpOverrideFn?: () => void;
  showForgotPasswordModal: (data: { email: string }) => void;
  getForgotPasswordUrl: (data: { email: string }) => LinkProps['to'];
  getThirdPartyLoginUrl: (
    provider: 'facebook' | 'google' | 'apple',
  ) => Promise<string> | string;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  submitLogin: FormSubmitFunction<any>;
  centered?: boolean;
  initialWarning?: string | null | undefined;
  compactView?: boolean;
};

export function LoginForm({
  analyticsTitle,
  usageContext,
  initialValues,
  thirdPartyLoginError,
  showSignupModal,
  getSignupUrl,
  signUpOverrideFn,
  showForgotPasswordModal,
  getForgotPasswordUrl,
  getThirdPartyLoginUrl,
  submitLogin,
  centered,
  initialWarning,
  compactView,
}: Props) {
  return (
    <Form
      analyticsTitle={analyticsTitle}
      usageContext={usageContext}
      data-qa-id="login-form"
      initialValues={initialValues}
      onSubmit={submitLogin}
      noScrollToError
      render={({ values: { email } }) => (
        <Box textAlign={centered ? 'center' : 'left'}>
          {initialWarning && (
            <InfoBox $type="warning" $marginTop={20} $marginBottom={20}>
              {initialWarning}
            </InfoBox>
          )}
          {thirdPartyLoginError && (
            <InfoBox className="error" $type="danger" $marginBottom={4}>
              {thirdPartyLoginError}
            </InfoBox>
          )}

          <Box mb={[16, null, 16]}>
            <BodySmall textAlign="center">
              {getText("Don't have an account?")}{' '}
              <LoginFormSignupLink
                email={email}
                getSignupUrl={getSignupUrl}
                signUpOverrideFn={signUpOverrideFn}
                showSignupModal={showSignupModal}
                formUsageContext={usageContext}
              >
                {getText('Sign up')}
              </LoginFormSignupLink>
            </BodySmall>
          </Box>

          <Box display="flex" flexDirection="column" mb={4} mt="-6px" mx="-6px">
            <Box m="6px">
              <GoogleButton
                onClick={async (e) => {
                  deleteCookie('userDismissedNotificationPreferencesDialog');
                  e.preventDefault();
                  const url = await getThirdPartyLoginUrl('google');
                  // @ts-expect-error TS(2322): Type 'string' is not assignable to type '(string |... Remove this comment to see the full error message
                  window.location = url;
                }}
                data-qa-id="google-oauth"
              />
            </Box>
            <Box m="6px">
              <FacebookButton
                onClick={async (e) => {
                  deleteCookie('userDismissedNotificationPreferencesDialog');
                  e.preventDefault();
                  const url = await getThirdPartyLoginUrl('facebook');
                  // @ts-expect-error TS(2322): Type 'string' is not assignable to type '(string |... Remove this comment to see the full error message
                  window.location = url;
                }}
                data-qa-id="facebook-oauth"
              />
            </Box>
            <Box m="6px">
              <AppleButton
                onClick={async (e) => {
                  deleteCookie('userDismissedNotificationPreferencesDialog');
                  e.preventDefault();
                  const url = await getThirdPartyLoginUrl('apple');
                  // @ts-expect-error TS(2322): Type 'string' is not assignable to type '(string |... Remove this comment to see the full error message
                  window.location = url;
                }}
                data-qa-id="apple-oauth"
              />
            </Box>
          </Box>

          {!compactView && <Divider marginTop={24} marginBottom={24} />}

          <GeneralError />

          <InputField
            name="email"
            type="email"
            dataModalFocus={usageContext === 'modal' ? 'true' : undefined}
            placeholder={getText('casey.smith@example.com')}
            label={
              /* TRANSLATORS: Login form */
              getText('Email')
            }
          />

          <InputField
            name="password"
            type="password"
            label={
              /* TRANSLATORS: Login form */
              getText('Password')
            }
          />

          <Box mt={[16, null, 40]} display="flex" justifyContent="flex-end">
            <SubmitButton fullWidth size="large">
              {
                /* TRANSLATORS: log in button */
                getText('Log in')
              }
            </SubmitButton>
          </Box>

          <Box mt={[16, null, 32]}>
            <BodySmall textAlign="center">
              <Box mt="8px">
                <LoginFormForgotPasswordLink
                  email={email}
                  formUsageContext={usageContext}
                  getForgotPasswordUrl={getForgotPasswordUrl}
                  showForgotPasswordModal={showForgotPasswordModal}
                >
                  {getText('I forgot my password')}
                </LoginFormForgotPasswordLink>
              </Box>
            </BodySmall>
          </Box>
        </Box>
      )}
    />
  );
}
